import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { movies, heroSlides } from "@/mocks/movies";
import { heroBackdrops } from "@/mocks/heroes";
import { useFavorites } from "@/hooks/useFavorites";
import type { Movie } from "@/types/movie";

const AUTOPLAY_MS = 7000;

export default function HeroSlider() {
  const slides = useMemo<Movie[]>(
    () =>
      heroSlides
        .map((id) => movies.find((movie) => movie.id === id))
        .filter((movie): movie is Movie => Boolean(movie)),
    [],
  );
  const [active, setActive] = useState(0);
  const { isFavorite, toggleFavorite } = useFavorites();

  const goTo = useCallback((index: number) => {
    setActive((index + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [active, slides.length]);

  const current = slides[active];
  if (!current) return null;
  const backdrop = heroBackdrops[current.id as keyof typeof heroBackdrops];
  const favorited = isFavorite(current.id);

  return (
    <section className="relative w-full overflow-hidden border-b border-background-200/70 bg-background-100">
      <div className="relative h-[560px] w-full md:h-[620px] lg:h-[680px]">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={index !== active}
          >
            <img
              src={heroBackdrops[slide.id as keyof typeof heroBackdrops]}
              alt={`${slide.title}（${slide.year}）剧照`}
              className={`h-full w-full object-cover object-center ${index === active ? "animate-slow-zoom" : ""}`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40"></div>
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-14 md:px-8 md:pb-16 lg:justify-center lg:pb-0">
          <div key={current.id} className="max-w-[620px] animate-fade-up">
            <h1 className="mb-4 flex items-center gap-2.5 font-label text-[11px] uppercase tracking-[0.3em] text-primary-300">
              <span className="h-px w-8 bg-primary-500/80"></span>
              日韩伦理电影
            </h1>

            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-600 px-2 py-1 font-label text-[10px] font-semibold tracking-widest text-background-50">
                焦点推荐
              </span>
              <span className="rounded-md border border-background-300/70 bg-background-100/60 px-2 py-1 text-[11px] text-foreground-600 backdrop-blur-sm">
                {current.year} · {current.country}
              </span>
              <span className="flex items-center gap-1 rounded-md border border-secondary-700/50 bg-background-100/60 px-2 py-1 font-label text-[11px] text-secondary-300 backdrop-blur-sm">
                <i className="ri-star-fill text-[10px] text-primary-400"></i>
                {current.rating.toFixed(1)}
              </span>
            </div>

            <h2 className="font-heading text-4xl font-semibold leading-[1.15] text-foreground-950 md:text-5xl lg:text-[54px]">
              {current.title}
            </h2>
            <p className="mt-2 font-label text-xs tracking-[0.2em] text-foreground-500">{current.originalTitle}</p>

            <p className="mt-5 max-w-[520px] text-[14px] leading-relaxed text-foreground-600 md:text-[15px]">
              {current.tagline}
            </p>

            <p className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-foreground-500">
              <span>导演 {current.director}</span>
              <span className="h-3 w-px bg-background-300"></span>
              <span>{current.duration} 分钟</span>
              <span className="h-3 w-px bg-background-300"></span>
              <span>{current.genres.join(" / ")}</span>
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-3 text-[13px] font-medium text-background-50 transition-colors hover:bg-primary-500"
              >
                查看剧情介绍
                <i className="ri-arrow-right-line"></i>
              </Link>
              <button
                type="button"
                onClick={() => toggleFavorite(current.id)}
                className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-[13px] transition-colors ${
                  favorited
                    ? "border-primary-500 bg-primary-950/50 text-primary-200"
                    : "border-background-300/80 text-foreground-700 hover:border-primary-500 hover:text-primary-200"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                {favorited ? "已收藏" : "收藏影片"}
              </button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 right-4 z-20 flex items-center gap-2 md:bottom-8 md:right-8">
          <button
            type="button"
            onClick={() => goTo(active - 1)}
            aria-label="上一部"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300/70 bg-background-50/60 text-foreground-700 backdrop-blur-sm transition-colors hover:border-primary-500 hover:text-primary-200"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            type="button"
            onClick={() => goTo(active + 1)}
            aria-label="下一部"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300/70 bg-background-50/60 text-foreground-700 backdrop-blur-sm transition-colors hover:border-primary-500 hover:text-primary-200"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
          <span className="ml-1 font-label text-[11px] tracking-widest text-foreground-500">
            {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
        </div>

        <div className="absolute bottom-6 left-4 z-20 hidden items-center gap-2 md:left-8 lg:flex">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goTo(index)}
              aria-label={`切换到 ${slide.title}`}
              className={`h-[3px] cursor-pointer rounded-full transition-all duration-300 ${
                index === active ? "w-10 bg-primary-500" : "w-5 bg-background-400/70 hover:bg-background-500"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}